import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';
import { handleStripeCardPayment } from '@/payment/stripePaymentHandler';
import { handleBraintreeCardPayment } from '@/payment/braintreePaymentHandler';
import { handleAmazonPayPayment } from '@/payment/amazonPayPaymentHandler';
import { handleGooglePayPayment } from '@/payment/googlePayPaymentHandler';
import { handleApplePayPayment } from '@/payment/applePayPaymentHandler';
import { handleLinkPayPayment } from '@/payment/linkPayPaymentHandler';
import { handlePayPalPayment } from '@/payment/paypalPaymentHandler';
import { handleKlarnaPayment } from '@/payment/klarnaPaymentHandler';
import { handleIdealPayment } from '@/payment/idealPaymentHandler';
import { handleVenmoPayment } from '@/payment/venmoPaymentHandler';
import type {
  GooglePayPaymentParams,
  ApplePayPaymentParams,
  LinkPayPaymentParams,
  PayPalPaymentParams,
  AmazonPayPaymentParams,
  KlarnaPaymentParams,
  IdealPaymentParams,
  VenmoPaymentParams,
  BlikPaymentParams,
} from '@/types/paymentHandlers';
import { handleAdyenCardPayment, handleAdyenPayment } from '@/payment/adyenPaymentHandle';
import {
  handleChargebeeCardPayment,
  handleChargebeeGenericPayment,
  type ChargebeeGenericPaymentParams,
} from '@/payment/chargebeePaymentHandler';
import { handleNuveiCardPayment } from '@/payment/nuveiPaymentHandler';
import { handleNuveiGenericPayment } from '@/payment/nuveiGenericPaymentHandle';
import { handleGenericStripePayment } from '@/payment/genericStirpePaymentHandle';
import { handleBlikCheckoutPayment } from '@/payment/blikCheckoutPaymentHandler';
import { handleBlikPayment } from '@/payment/blikPaymentHandler';
import { handleAppchargePaymentElementCardPayment } from '@/payment/appchargePaymentElementHandler';
import { isAppchargeNewCardPayment } from '@/utils/appchargePaymentElement';
import type { StripeElements } from '@stripe/stripe-js';

type PaymentHandler = (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse = unknown>(params: unknown) => Promise<TResponse>,
  params?: unknown
) => Promise<unknown>;

// Bespoke handlers — methods whose flow is provider-specific in a way the
// long-tail provider dispatch can't replicate (custom SDKs, wallet APIs,
// multi-provider methods like CREDIT_CARD, or workarounds like BLIK_CHECKOUT).
// Anything NOT in this map falls through to `dispatchByProvider`, which routes
// `provider === STRIPE` → handleGenericStripePayment, ADYEN → handleAdyenPayment,
// NUVEI → handleNuveiGenericPayment. Adding a new APM whose provider is
// already mapped is a zero-FE-change op.
const BESPOKE_HANDLERS: Partial<Record<PaymentMethodEnum, PaymentHandler>> = {
  [PaymentMethodEnum.CREDIT_CARD]: async (checkout, sendPaymentToServer, params) => {
    const cardPaymentSettings = checkout.paymentMethodsSettings.find(
      (pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
    );

    if (isAppchargeNewCardPayment(checkout, params)) {
      return await handleAppchargePaymentElementCardPayment(checkout, sendPaymentToServer);
    }

    if (cardPaymentSettings?.provider === ProviderEnum.STRIPE) {
      return await handleStripeCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
    }
    if (cardPaymentSettings?.provider === ProviderEnum.BRAINTREE) {
      return await handleBraintreeCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
    }
    if (cardPaymentSettings?.provider === ProviderEnum.ADYEN) {
      return await handleAdyenCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
    }
    if (cardPaymentSettings?.provider === ProviderEnum.NUVEI) {
      return await handleNuveiCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
    }
    if (cardPaymentSettings?.provider === ProviderEnum.CHARGEBEE) {
      return await handleChargebeeCardPayment(checkout, sendPaymentToServer);
    }
  },
  [PaymentMethodEnum.AMAZON_PAY]: (checkout, sendPaymentToServer, params) =>
    handleAmazonPayPayment(checkout, sendPaymentToServer, params as AmazonPayPaymentParams),
  [PaymentMethodEnum.GOOGLE_PAY]: (checkout, sendPaymentToServer, params) =>
    handleGooglePayPayment(checkout, sendPaymentToServer, params as GooglePayPaymentParams),
  [PaymentMethodEnum.APPLE_PAY]: (checkout, sendPaymentToServer, params) =>
    handleApplePayPayment(checkout, sendPaymentToServer, params as ApplePayPaymentParams),
  [PaymentMethodEnum.LINK]: (checkout, sendPaymentToServer, params) =>
    handleLinkPayPayment(checkout, sendPaymentToServer, params as LinkPayPaymentParams),
  [PaymentMethodEnum.PAYPAL]: (checkout, sendPaymentToServer, params) =>
    handlePayPalPayment(checkout, sendPaymentToServer, params as PayPalPaymentParams),
  [PaymentMethodEnum.KLARNA]: (checkout, sendPaymentToServer, params) =>
    handleKlarnaPayment(checkout, sendPaymentToServer, params as KlarnaPaymentParams),
  [PaymentMethodEnum.IDEAL]: (checkout, sendPaymentToServer, params) =>
    handleIdealPayment(checkout, sendPaymentToServer, params as IdealPaymentParams),
  [PaymentMethodEnum.VENMO]: (checkout, sendPaymentToServer, params) =>
    handleVenmoPayment(checkout, sendPaymentToServer, params as VenmoPaymentParams),
  [PaymentMethodEnum.BLIK_CHECKOUT]: (checkout, sendPaymentToServer) =>
    handleBlikCheckoutPayment(checkout, sendPaymentToServer),
  [PaymentMethodEnum.BLIK]: (checkout, sendPaymentToServer, params) =>
    handleBlikPayment(checkout, sendPaymentToServer, params as BlikPaymentParams),
};

const dispatchByProvider: PaymentHandler = async (checkout, sendPaymentToServer, params) => {
  const selectedPaymentMethod =
    checkout.paymentMethodSelected === PaymentMethodEnum.OTHER
      ? checkout.otherMethodSelected
      : checkout.paymentMethodSelected;
  const settings = checkout.paymentMethodsSettings.find((pm) => pm.paymentMethodName === selectedPaymentMethod);

  switch (settings?.provider) {
    case ProviderEnum.STRIPE:
      return handleGenericStripePayment(
        checkout,
        sendPaymentToServer,
        params as { elements?: StripeElements; email?: string; name?: string }
      );
    case ProviderEnum.ADYEN:
      return handleAdyenPayment(checkout, sendPaymentToServer, params as Record<string, string>);
    case ProviderEnum.NUVEI:
      return handleNuveiGenericPayment(checkout, sendPaymentToServer, params as Record<string, string>);
    // Subscriptions: the gateway's routed APMs (Kakao Pay, Naver Pay, KR card, …)
    // through Chargebee's Payment Component.
    case ProviderEnum.CHARGEBEE:
      return handleChargebeeGenericPayment(checkout, sendPaymentToServer, params as ChargebeeGenericPaymentParams);
    default:
      throw new Error(`No handler found for payment method: ${selectedPaymentMethod}`);
  }
};

class PaymentHandlerService {
  async processPayment(
    paymentMethod: PaymentMethodEnum,
    checkout: ICheckoutContext,
    sendPaymentToServer: <TResponse = unknown>(params: unknown) => Promise<TResponse>,
    params?: unknown
  ): Promise<unknown> {
    const handler = BESPOKE_HANDLERS[paymentMethod] ?? dispatchByProvider;
    return await handler(checkout, sendPaymentToServer, params);
  }
}

export const paymentHandler = new PaymentHandlerService();
